import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { eur } from "../lib/api";
import { PROPERTY_TYPES, PURPOSES, PROPERTY_STATUS, fmtDate, authUrl, canCreate, numOrNull } from "../lib/re";
import { Badge, Spinner, Empty, Pagination, Btn, DateInput } from "../components/re/ui";
import { Plus, LayoutGrid, List, Download, SlidersHorizontal, Search, Home, BedDouble, Ruler, FileWarning, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY_F = { q: "", purpose: "", property_type: "", status: "", location: "", price_min: "", price_max: "",
  area_min: "", area_max: "", bedrooms: "", consultant_id: "", docs: "", acq_from: "", acq_to: "", archived: false };

function buildParams(f) {
  const p = {};
  Object.entries(f).forEach(([k, v]) => {
    if (v === "" || v === null || v === false) return;
    p[k] = ["price_min", "price_max", "area_min", "area_max", "bedrooms"].includes(k) ? numOrNull(v) : v;
  });
  return p;
}

const priceOf = (p) => (p.sale_price ? eur(p.sale_price) : p.rent_price ? `${eur(p.rent_price)}/mês` : "Preço sob consulta");

function PropertyCard({ p, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`property-card-${p.id}`} className="card-almar overflow-hidden text-left group transition-transform hover:-translate-y-0.5">
      <div className="aspect-[4/3] bg-[#0A1622] relative overflow-hidden">
        {p.cover_url ? <img src={authUrl(p.cover_url)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center text-slate-600"><Home size={40} /></div>}
        <div className="absolute top-3 left-3 bg-[#0D1B2A]/90 rounded-full"><Badge value={p.status} label={PROPERTY_STATUS[p.status]} /></div>
        <span className="absolute bottom-3 left-3 text-[11px] font-mono bg-black/60 text-slate-200 px-2 py-0.5 rounded">{p.reference}</span>
        {!p.docs_complete && <span title="Documentação incompleta" className="absolute top-3 right-3 bg-black/60 rounded-full p-1.5 text-amber-300"><FileWarning size={14} /></span>}
      </div>
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-wider text-[#C9A84C]">{PROPERTY_TYPES[p.property_type]} · {PURPOSES[p.purpose]}</div>
        <h3 className="text-slate-100 font-semibold mt-1 truncate">{p.title}</h3>
        <p className="text-xs text-slate-400 truncate">{[p.parish, p.municipality].filter(Boolean).join(", ") || "Localização por definir"}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="text-[#E0C574] font-semibold">{priceOf(p)}</span>
          <span className="flex gap-3 text-xs text-slate-400">
            {p.bedrooms != null && <span className="flex items-center gap-1"><BedDouble size={13} />{p.bedrooms}</span>}
            {p.area_useful != null && <span className="flex items-center gap-1"><Ruler size={13} />{p.area_useful} m²</span>}
          </span>
        </div>
      </div>
    </button>
  );
}

function PropertyTable({ items, onOpen }) {
  return (
    <div className="card-almar overflow-x-auto">
      <table className="w-full text-sm" data-testid="properties-table">
        <thead className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-[#1E3A56]">
          <tr>{["", "Referência", "Imóvel", "Localização", "Preço", "Estado", "Consultor", "Angariação", "Docs"].map((h) => <th key={h} className="text-left px-3 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} onClick={() => onOpen(p)} data-testid={`property-row-${p.id}`} className="border-b border-[#1E3A56]/50 hover:bg-[#0D1B2A]/60 cursor-pointer">
              <td className="px-3 py-2">{p.cover_url ? <img src={authUrl(p.cover_url)} alt="" className="w-14 h-10 object-cover rounded" /> : <div className="w-14 h-10 rounded bg-[#0A1622]" />}</td>
              <td className="px-3 py-2 font-mono text-xs text-slate-300">{p.reference}</td>
              <td className="px-3 py-2"><div className="text-slate-100 max-w-[220px] truncate">{p.title}</div><div className="text-[11px] text-slate-500">{PROPERTY_TYPES[p.property_type]} · {PURPOSES[p.purpose]}</div></td>
              <td className="px-3 py-2 text-slate-400">{p.municipality || "—"}</td>
              <td className="px-3 py-2 text-[#E0C574] whitespace-nowrap">{priceOf(p)}</td>
              <td className="px-3 py-2"><Badge value={p.status} label={PROPERTY_STATUS[p.status]} /></td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{p.consultant_name || "—"}</td>
              <td className="px-3 py-2 text-slate-400">{fmtDate(p.acquisition_date)}</td>
              <td className="px-3 py-2">{p.docs_complete ? <span className="text-emerald-400 text-xs">Completa</span> : <span className="text-amber-300 text-xs">Incompleta</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Filters({ f, set, users, onReset }) {
  const inp = (k, ph, type = "text") => (
    <input type={type} className="input-almar py-2 text-sm" placeholder={ph} value={f[k]} data-testid={`filter-${k}`} onChange={(e) => set({ ...f, [k]: e.target.value })} />
  );
  const sel = (k, opts, ph) => (
    <select className="input-almar py-2 text-sm" value={f[k]} data-testid={`filter-${k}`} onChange={(e) => set({ ...f, [k]: e.target.value })}>
      <option value="">{ph}</option>
      {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="card-almar p-4 mb-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="filters-panel">
      {sel("purpose", { venda: "Venda", arrendamento: "Arrendamento" }, "Venda / Arrendamento")}
      {sel("property_type", PROPERTY_TYPES, "Tipo de imóvel")}
      {sel("status", PROPERTY_STATUS, "Estado")}
      {inp("location", "Localização")}
      {inp("price_min", "Preço mín. €", "number")}
      {inp("price_max", "Preço máx. €", "number")}
      {inp("area_min", "Área mín. m²", "number")}
      {inp("area_max", "Área máx. m²", "number")}
      {inp("bedrooms", "Quartos (mín.)", "number")}
      {sel("consultant_id", Object.fromEntries(users.map((u) => [u.id, u.name])), "Consultor")}
      {sel("docs", { completa: "Documentação completa", incompleta: "Documentação incompleta" }, "Documentação")}
      <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.archived} data-testid="filter-archived" onChange={(e) => set({ ...f, archived: e.target.checked })} /> Arquivados</label>
      <div><span className="text-[10px] text-slate-500">Angariação desde</span><DateInput className="py-2 text-sm" value={f.acq_from} onChange={(v) => set({ ...f, acq_from: v })} testId="filter-acq_from" /></div>
      <div><span className="text-[10px] text-slate-500">Angariação até</span><DateInput className="py-2 text-sm" value={f.acq_to} onChange={(v) => set({ ...f, acq_to: v })} testId="filter-acq_to" /></div>
      <div className="flex items-end"><Btn variant="ghost" onClick={onReset} data-testid="filters-reset-btn"><X size={14} /> Limpar</Btn></div>
    </div>
  );
}

import { AiBatchButton } from "../components/re/AiText";

export default function Imoveis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState(localStorage.getItem("almar_prop_view") || "cards");
  const [f, setF] = useState(EMPTY_F);
  const [showF, setShowF] = useState(false);
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setData(null);
    api.get("/re/properties", { params: { ...buildParams(f), page, sort, page_size: 12 } })
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Não foi possível carregar os imóveis"); setData({ items: [], total: 0, pages: 1, page: 1 }); });
  }, [f, page, sort]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setFilters = (nf) => { setF(nf); setPage(1); };
  const changeView = (v) => { setView(v); localStorage.setItem("almar_prop_view", v); };
  const exportCsv = () => {
    const qs = new URLSearchParams(buildParams(f)).toString();
    window.open(`${authUrl("/re/properties/export")}&${qs}`, "_blank");
  };
  const open = (p) => navigate(`/imoveis/${p.id}`);

  return (
    <Layout title="Imóveis">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="input-almar pl-9" placeholder="Pesquisar por referência, título ou localização" value={f.q} data-testid="property-search-input" onChange={(e) => setFilters({ ...f, q: e.target.value })} />
        </div>
        <div className="flex flex-wrap lg:flex-nowrap gap-2">
          <Btn variant="ghost" onClick={() => setShowF(!showF)} data-testid="toggle-filters-btn"><SlidersHorizontal size={15} /> Filtros</Btn>
          <select className="input-almar py-2 text-sm w-auto" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="property-sort-select">
            <option value="recent">Mais recentes</option><option value="acq">Data de angariação</option>
            <option value="price_desc">Preço ↓</option><option value="price_asc">Preço ↑</option><option value="ref">Referência</option>
          </select>
          <div className="flex rounded-lg border border-[#1E3A56] overflow-hidden">
            <button onClick={() => changeView("cards")} data-testid="view-cards-btn" className={`px-3 ${view === "cards" ? "bg-[#C9A84C]/20 text-[#E0C574]" : "text-slate-400"}`}><LayoutGrid size={16} /></button>
            <button onClick={() => changeView("table")} data-testid="view-table-btn" className={`px-3 ${view === "table" ? "bg-[#C9A84C]/20 text-[#E0C574]" : "text-slate-400"}`}><List size={16} /></button>
          </div>
          {user.role !== "leitura" && <Btn variant="ghost" onClick={exportCsv} data-testid="export-properties-btn"><Download size={15} /> Exportar</Btn>}
          {canCreate(user) && <AiBatchButton />}
          {canCreate(user) && <Btn onClick={() => navigate("/imoveis/novo")} data-testid="new-property-btn"><Plus size={16} /> Novo imóvel</Btn>}
        </div>
      </div>
      {showF && <Filters f={f} set={setFilters} users={users} onReset={() => setFilters(EMPTY_F)} />}
      {!data ? <Spinner /> : data.items.length === 0 ? (
        <Empty text="Nenhum imóvel corresponde aos critérios." action={canCreate(user) && <Btn onClick={() => navigate("/imoveis/novo")}><Plus size={15} /> Adicionar imóvel</Btn>} />
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="properties-grid">
          {data.items.map((p) => <PropertyCard key={p.id} p={p} onOpen={() => open(p)} />)}
        </div>
      ) : <PropertyTable items={data.items} onOpen={open} />}
      {data && data.items.length > 0 && <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />}
    </Layout>
  );
}
